import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Phone, ChevronDown, ShieldCheck, MessageCircle } from 'lucide-react';
import { LandkreisSearch } from './LandkreisSearch';
import { heroPromises, socialLinks } from '../data/homeContent';
import { HERO_SLIDES, HeroSlides, useHeroSlide } from './HeroSlides';
import { Link } from 'react-router-dom';
import { trackEvent, analyticsEvents, contactInfo } from '../data/mock';
import { FormNames, trackFormSuccess } from '../utils/gtm';

const HeroSection = () => {
  const navigate = useNavigate();
  const [slide, setSlide] = useHeroSlide();
  const current = HERO_SLIDES[slide];

  const startValuation = (county, rawQuery) => {
    trackEvent(analyticsEvents.HERO_CTA_CLICK, { cta_type: 'landkreis_search', matched: Boolean(county) });
    if (county) trackFormSuccess(FormNames.QUICK_SEARCH, county);
    navigate('/bewertung', { state: { prefill: county, query: rawQuery, from: 'hero' } });
  };

  const handlePhoneClick = () => {
    trackEvent(analyticsEvents.PHONE_CLICK, { location: 'hero' });
    window.location.href = contactInfo.phoneLink;
  };

  return (
    <section className="hv-hero hv-dark relative overflow-hidden" data-testid="homepage-hero">
      <HeroSlides index={slide} />

      <div className="container mx-auto px-4 relative z-10">
        <div className="hv-hero-inner grid lg:grid-cols-12 gap-10 lg:gap-12 items-center min-h-[calc(100svh-80px)] max-h-[900px] pt-14 pb-8 md:pb-24 lg:pt-20 lg:pb-28">
          <div className="lg:col-span-7">
            <p className="hv-kicker text-xs font-semibold uppercase tracking-[0.18em] text-[#DFC590] mb-6" data-testid="hero-kicker">
              Fachmakler für Ackerland, Wald, Grünland und Bauernhöfe
            </p>
            <h1 className="hv-hero-title text-[#F8F6F0] mb-7" data-testid="hero-heading">
              Was ist <span className="hv-hero-word" key={current.key} data-testid="hero-word">{current.word}</span> <em className="hv-accent">heute wert?</em>
            </h1>
            <p className="text-lg lg:text-xl text-[#E7EBE4] leading-relaxed mb-8 max-w-[50ch]" data-testid="hero-introduction">
              Eine ehrliche Ersteinschätzung von einem Makler, der nur Agrarflächen vermittelt. Kostenlos, diskret
              und ohne Verpflichtung – auch wenn das Ergebnis lautet: behalten.
            </p>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <button type="button" onClick={handlePhoneClick} data-testid="hero-phone" className="hv-link inline-flex items-center gap-2 text-[#F8F6F0] font-medium">
                <Phone className="w-4 h-4 text-[#DFC590]" aria-hidden="true" />
                Lieber anrufen? {contactInfo.phone}
              </button>
              <button type="button" onClick={() => navigate('/suchprofil')} data-testid="hero-buyer-profile-link" className="hv-link text-[#C9D3C8] underline underline-offset-4 decoration-[#DFC590]/60">
                Sie suchen Flächen? Suchprofil anlegen
              </button>
              <span className="inline-flex items-center gap-2">
                {socialLinks.map((sl) => (
                  <a key={sl.key} href={sl.url} target="_blank" rel="noopener noreferrer" aria-label={`AgrarKapital auf ${sl.label}`} data-testid={`hero-social-${sl.key}`} onClick={() => trackEvent(analyticsEvents.SOCIAL_CLICK, { platform: sl.key, location: 'hero' })} className="hv-icon-btn hv-icon-btn-light w-10 h-10 !min-h-0">
                    <sl.icon className="w-4 h-4" aria-hidden="true" />
                  </a>
                ))}
              </span>
            </div>
          </div>

          <div className="lg:col-span-5 lg:justify-self-end w-full max-w-[520px]">
            <div className="hv-hero-card bg-[#FFFEFA] rounded-[6px] p-7 lg:p-9 shadow-[0_40px_80px_-30px_rgba(0,0,0,.55)]" data-testid="hero-card">
              <div className="flex items-center gap-3 mb-5">
                <ShieldCheck className="w-5 h-5 text-[#1C432F]" aria-hidden="true" />
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#647064]">Kostenlose Ersteinschätzung</p>
              </div>
              <h2 className="hv-hero-card-title text-[#19392B] mb-2">In welchem Landkreis liegt Ihre Fläche?</h2>
              <p className="text-[15px] text-[#647064] mb-6">Mehr brauchen wir für den Anfang nicht. Alles Weitere klären wir im Formular oder am Telefon.</p>
              <LandkreisSearch onSubmit={startValuation} size="md" buttonLabel="Bewertung starten" />
              <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp" onClick={() => trackEvent(analyticsEvents.SOCIAL_CLICK, { platform: 'whatsapp', location: 'hero_card' })} className="hv-link mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#19392B]">
                <MessageCircle className="w-4 h-4 text-[#1C432F]" aria-hidden="true" />Lieber per WhatsApp? Kurz schreiben, wir melden uns.
              </a>
              <ul className="hv-hero-promises mt-6 space-y-2" data-testid="hero-promises">
                {heroPromises.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm text-[#3F4F43]">
                    <Check className="w-4 h-4 text-[#1C432F]" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="hv-hero-dots relative md:absolute md:bottom-6 md:inset-x-0 z-10 container mx-auto px-4 pb-6 md:pb-0 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Flächenarten" data-testid="hero-slide-nav">
          {HERO_SLIDES.map((s, i) => (
            <button key={s.key} type="button" role="tab" aria-selected={i === slide} onClick={() => setSlide(i)} data-testid={`hero-slide-btn-${s.key}`}
              className={`hv-slide-btn text-xs uppercase tracking-[0.14em] px-3 py-1.5 rounded-full border transition-colors ${i === slide ? 'border-[#DFC590] text-[#F8F6F0] bg-[#DFC590]/15' : 'border-[#F8F6F0]/20 text-[#C9D3C8] hover:border-[#F8F6F0]/50'}`}>
              {s.label}
            </button>
          ))}
          <Link to={current.to} className="text-xs text-[#DFC590] underline underline-offset-4 ml-1" data-testid="hero-slide-link">Mehr zu {current.label} →</Link>
        </div>
        <div className="hv-hero-scroll hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C9D3C8]" aria-hidden="true">
          Entdecken <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
